import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { startEmailSignUp, attemptSignUpEmailCode } from '@/lib/clerk-auth-flows';
import { waitForClerkSessionResolution, getClerkAuthUser } from '@/lib/clerk-token';
import {
  ACCOUNT_LOCKED_MESSAGE,
  SESSION_VERIFY_FAILED_MESSAGE,
} from '@/providers/auth-context';
import { analytics } from '@/lib/analytics';
import { EXPERIMENTS } from '@/lib/analytics/experiments';
import { pushCompleteRegistration } from '@/lib/analytics/gtm';
import { resolveEntrySource, type ReklaimAttribution } from '@/lib/analytics/properties';
import { grantImpliedConsent } from '@/lib/cookieConsent';
import { getDetectedCountry } from '@/lib/geo';
import { grantConsent } from '@/lib/recordConsent';
import { retiredSupabaseEmailOtpError } from './useAuth';

/**
 * Active experiment assignments for W1-0 funnel props (REK-2146).
 * Empty object when no registered experiment has a string variant yet.
 */
function getExperimentVariantsMap(): Record<string, string> {
  const variants: Record<string, string> = {};
  const cookieVariant = analytics.getFeatureFlag(EXPERIMENTS.COOKIE_CONSENT_VARIANT);
  if (typeof cookieVariant === 'string' && cookieVariant.length > 0) {
    variants[EXPERIMENTS.COOKIE_CONSENT_VARIANT] = cookieVariant;
  }
  return variants;
}

function attributionFromOptions(options?: UseFastAuthOptions): Partial<ReklaimAttribution> {
  return {
    source: options?.source,
    medium: options?.medium,
    campaign: options?.campaign,
    ref: options?.referralCode ?? undefined,
    fbclid: options?.fbclid,
    campaign_id: options?.campaignId,
    adset_id: options?.adsetId,
    ad_id: options?.adId,
  };
}

/**
 * REK-1986 (SSO.5): after the sign-up code verifies and the session
 * activates, the SSO.2 clerk-webhook still has to provision the shadow
 * auth.users + profiles rows. ClerkAuthProvider polls for them for up to
 * 90s (PROFILE_POLL_TIMEOUT_MS) while this flow shows the existing
 * "Creating your account…" state — wait slightly longer so the provider
 * always reports a terminal state before we give up.
 */
const CLERK_SIGN_UP_RESOLUTION_TIMEOUT_MS = 100_000;

export type UserIntent = 'rewards' | 'jackpot' | 'exposure';

export interface BreachData {
  name: string;
  date: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  dataTypes: string[];
}

export interface ProfileAttribute {
  key: string;
  value: string;
  source: string;
}

// Unveil API types
export interface UnveilAddress {
  address: string;
  city: string;
  state: string;
  zip: string;
}

export interface AccountAttributes {
  first_name: string | null;
  last_name: string | null;
  gender: string | null;
  age: string | null;
  marital_status: string | null;
  education_level: string | null;
  addresses: UnveilAddress[];
  number_of_children: string | null;
}

export interface LookupResponse {
  email: string;
  hasBreaches: boolean;
  breachData: BreachData[];
  hasProfile: boolean;
  profileData: ProfileAttribute[];
  entriesAwarded: number;
  calculation: {
    base: number;
    profileBonus: number;
    breachBonus: number;
    criticalBonus: number;
  };
  lookupType: 'breach' | 'profile' | 'both' | 'simulated';
  // Unveil API data
  accountAttributes: AccountAttributes | null;
  hasUnveilData: boolean;
  // Count data for onboarding display
  brandCount?: number;
  dataBrokerCount?: number;
  // Country detected from CF-IPCountry header (ISO 3166-1 alpha-2)
  detectedCountry?: string;
}

export type FastAuthStep = 'intent' | 'email' | 'loading' | 'reveal' | 'magic-link' | 'creating' | 'complete';

export interface FastAuthState {
  step: FastAuthStep;
  email: string | null;
  otpEmail: string | null;
  isExistingUser: boolean;
  intent: UserIntent[];
  lookupData: LookupResponse | null;
  entriesAwarded: number;
  isLoading: boolean;
  error: string | null;
  linkSent: boolean;
  resendCooldown: number;
}

interface UseFastAuthOptions {
  referralCode?: string | null;
  source?: string;
  medium?: string;
  campaign?: string;
  /** Meta click id from `reklaim_attribution.fbclid` (REK-2146). */
  fbclid?: string;
  /** Meta campaign id from `reklaim_attribution.campaign_id` (REK-2146). */
  campaignId?: string;
  /** Meta adset id from `reklaim_attribution.adset_id` (REK-2146). */
  adsetId?: string;
  /** Meta ad id from `reklaim_attribution.ad_id` (REK-2146). */
  adId?: string;
  initialEmail?: string;
  sourcePage?: string;
  // REK-1924: same-origin destination used as the signup magic-link
  // `redirectTo`, so clicking the emailed link lands a new user on a survey
  // deep link. Undefined preserves the legacy '/' redirect.
  next?: string;
  onComplete?: () => void;
  onAccountLocked?: (message: string) => void;
}

export function useFastAuth(options?: UseFastAuthOptions) {
  // If initialEmail is provided, start at email step (user must enter name + DOB before scan)
  const initialStep: FastAuthStep = options?.initialEmail ? 'email' : 'intent';
  const signupStartedAt = useRef<number | null>(null);
  // REK-1727: track emails already tagged in Braze for this funnel so OTP
  // resends (which reuse `sendOtp`) don't emit duplicate `unveil_start` events.
  const brazeTaggedEmails = useRef<Set<string>>(new Set());

  // Keep attribution inputs fresh for memoized funnel callbacks (REK-2146).
  const attributionInputs = useMemo(
    () => attributionFromOptions(options),
    [
      options?.source,
      options?.medium,
      options?.campaign,
      options?.referralCode,
      options?.fbclid,
      options?.campaignId,
      options?.adsetId,
      options?.adId,
    ],
  );
  
  const [state, setState] = useState<FastAuthState>({
    step: initialStep,
    email: options?.initialEmail || null,
    otpEmail: null,
    isExistingUser: false,
    intent: options?.initialEmail ? ['rewards', 'jackpot', 'exposure'] : [],
    lookupData: null,
    entriesAwarded: 0,
    isLoading: false,
    error: null,
    linkSent: false,
    resendCooldown: 0,
  });

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (state.resendCooldown > 0) {
      const timer = setTimeout(() => {
        setState(prev => ({ ...prev, resendCooldown: prev.resendCooldown - 1 }));
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [state.resendCooldown]);

  const setIntent = useCallback((intents: UserIntent[]) => {
    setState(prev => ({ 
      ...prev, 
      intent: intents, 
      step: 'email',
      error: null 
    }));
  }, []);

  const sourcePage = options?.sourcePage || 'onboarding';

  const submitEmail = useCallback(async (email: string, intents?: UserIntent[]): Promise<boolean> => {
    const usedIntents = intents || state.intent;
    setState(prev => ({ ...prev, step: 'loading', email, intent: usedIntents, isLoading: true, error: null }));

    // scanStart measures the full server round-trip (NeverBounce gate + scan)
    // for `exposure_scan_completed`. The scan-start / signup-start events are
    // NOT fired here: when NeverBounce blocks the email we return on the
    // `emailValidationFailed` branch below without ever running HIBP/Unveil, so
    // emitting them up-front would count scans/signups that never happened and
    // poison the funnel. They are fired only once validation passes.
    const scanStart = Date.now();

    try {
      const response = await supabase.functions.invoke('email-lookup', {
        // validateEmail: gate the signup email through NeverBounce server-side
        // (fail-open). A clean invalid/disposable verdict short-circuits with
        // `emailValidationFailed` so we keep the user on the email step.
        body: { email, intent: usedIntents, country: getDetectedCountry(), validateEmail: true },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Lookup failed');
      }

      // NeverBounce rejected the email (invalid/disposable). Return to the email
      // step with an inline error; do NOT proceed to the scan/reveal or fire the
      // Braze tag for a bad email. Scan/signup analytics have not fired yet, so
      // a blocked email leaves no orphaned `exposure_scan_started` event.
      if (response.data && (response.data as { emailValidationFailed?: boolean }).emailValidationFailed) {
        const message = (response.data as { error?: string }).error || 'Please enter a valid email address';
        setState(prev => ({ ...prev, step: 'email', isLoading: false, error: message }));
        return false;
      }

      const data = response.data as LookupResponse;

      // Validation passed and the scan ran — now emit the funnel-start events.
      signupStartedAt.current = scanStart;
      analytics.trackEvent({ name: 'exposure_scan_started', properties: {} });
      analytics.trackEvent({
        name: 'signup_started',
        properties: {
          source_page: sourcePage,
          cta_location: 'email_input',
          auth_method: 'email_otp',
          experiment_variants: getExperimentVariantsMap(),
          entry_source: resolveEntrySource(attributionInputs),
        },
      });

      analytics.trackEvent({
        name: 'exposure_scan_completed',
        properties: {
          brokers_found: data.brokerExposures?.length ?? 0,
          breaches_found: data.breaches?.length ?? 0,
          scan_duration: Date.now() - scanStart,
        },
      });

      setState(prev => ({
        ...prev,
        step: 'reveal',
        lookupData: data,
        entriesAwarded: data.entriesAwarded,
        isLoading: false,
      }));
      return true;
    } catch (error) {
      console.error('Email lookup error:', error);
      setState(prev => ({
        ...prev,
        step: 'email',
        isLoading: false,
        error: error instanceof Error ? error.message : 'Something went wrong',
      }));
      return false;
    }
  }, [state.intent, sourcePage, attributionInputs]);

  const sendOtp = useCallback(async () => {
    if (!state.email) return;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      let isExistingUser = false;
      // Drive "sent" / verify UX from the submitted address (REK-1732) —
      // never trust a primary leaked via signup-otp.
      const verifyEmail = state.email;

      // REK-1986 (SSO.5): Clerk custom sign-up — signUp.create (smart
      // CAPTCHA runs against the `<div id="clerk-captcha">` mounted on the
      // reveal step) + prepareEmailAddressVerification sends the 6-digit
      // code. `form_identifier_exists` → existing-account redirect is a
      // residual Clerk surface (Deferred on REK-1732).
      const result = await startEmailSignUp({
        email: state.email,
        firstName: localStorage.getItem('reklaim_first_name') || undefined,
        lastName: localStorage.getItem('reklaim_last_name') || undefined,
      });
      if (result.status === 'error') throw new Error(result.error.message);
      isExistingUser = result.status === 'user_exists';

      // REK-1727 / REK-1734 / REK-1732: Braze tag fires here (not on
      // email-lookup). Legacy multi-email-auth no longer discloses
      // existingUser, so we omit unveil on the Supabase-edge path (no
      // replacement existence probe). Clerk still gates on
      // form_identifier_exists (Deferred residual). Guarded by
      // `brazeTaggedEmails` so OTP resends don't emit duplicate unveil_start.
      if (
        !isExistingUser &&
        typeof localStorage !== 'undefined' &&
        localStorage.getItem('reklaim_marketing_consent') === 'true' &&
        !brazeTaggedEmails.current.has(state.email)
      ) {
        // Add to the dedupe set up-front to prevent overlapping retries from
        // the same funnel session, but roll it back if the invoke fails so a
        // subsequent OTP resend can try again. Without that rollback a single
        // transient `unveil-braze` failure would permanently suppress the
        // Braze tag for this user.
        const tagEmail = state.email;
        brazeTaggedEmails.current.add(tagEmail);
        const country = state.lookupData?.detectedCountry || getDetectedCountry();
        const firstName = localStorage.getItem('reklaim_first_name') || undefined;
        const lastName = localStorage.getItem('reklaim_last_name') || undefined;
        const dob = localStorage.getItem('reklaim_dob') || undefined;
        try {
          const tagPromise = supabase.functions.invoke('unveil-braze', {
            body: {
              action: 'tag',
              consent: true,
              email: tagEmail,
              firstName,
              lastName,
              dob,
              country,
            },
          });
          if (tagPromise && typeof (tagPromise as Promise<{ error?: unknown }>).then === 'function') {
            (tagPromise as Promise<{ error?: unknown }>)
              .then((result) => {
                if (result && result.error) {
                  brazeTaggedEmails.current.delete(tagEmail);
                  console.warn('unveil-braze tag invoke returned error (non-fatal):', result.error);
                }
              })
              .catch((err) => {
                brazeTaggedEmails.current.delete(tagEmail);
                console.warn('unveil-braze tag invoke failed (non-fatal):', err);
              });
          }
        } catch (err) {
          brazeTaggedEmails.current.delete(tagEmail);
          console.warn('unveil-braze tag invoke threw synchronously (non-fatal):', err);
        }
      }

      setState(prev => ({ 
        ...prev, 
        step: 'magic-link', 
        otpEmail: verifyEmail,
        isExistingUser,
        linkSent: true,
        resendCooldown: 30,
        isLoading: false 
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to send link',
      }));
    }
  }, [state.email, state.lookupData?.detectedCountry]);

  const verifyOtp = useCallback(async (otpCode: string) => {
    const emailForVerify = state.otpEmail || state.email;
    if (!emailForVerify) return;

    setState(prev => ({ ...prev, step: 'creating', isLoading: true, error: null }));
    
    try {
      // Signup implies analytics-only consent (REK-3389; marketing needs explicit opt-in). Must opt-in PostHog BEFORE
      // setSession/verifyOtp (or the Clerk session activation) because those
      // trigger the auth provider's sign-in handler which calls
      // posthog.identify(). If PostHog is still opted-out at that point,
      // identify() is silently dropped and the user is invisible.
      // Only applied after verification succeeds to avoid persisting consent on failed OTP.
      // REK-1489: shared implementation in cookieConsent.ts so the magic-link
      // path on /auth/confirm uses byte-identical semantics.
      let didAutoConsent = false;
      const applyImpliedConsent = () => {
        if (grantImpliedConsent('otp_login')) {
          didAutoConsent = true;
        }
      };

      // REK-1986 (SSO.5): attemptEmailAddressVerification + setActive
      // replace the legacy verify-otp/setSession pair. After activation
      // the SSO.2 clerk-webhook provisions the shadow auth.users +
      // profiles rows; ClerkAuthProvider polls for them (the existing
      // "Creating your account…" state covers the wait) and reports the
      // terminal outcome we block on here.
      const attempt = await attemptSignUpEmailCode(otpCode, {
        onBeforeSessionActivate: applyImpliedConsent,
      });
      if (attempt.status === 'error') {
        if (attempt.error.accountLocked) {
          const err = new Error(attempt.error.message);
          err.name = 'AccountLockedError';
          throw err;
        }
        throw new Error(attempt.error.message);
      }
      const resolution = await waitForClerkSessionResolution(
        CLERK_SIGN_UP_RESOLUTION_TIMEOUT_MS,
      );
      if (resolution === 'denied-locked') {
        const err = new Error(ACCOUNT_LOCKED_MESSAGE);
        err.name = 'AccountLockedError';
        throw err;
      }
      if (resolution !== 'granted') {
        throw new Error(SESSION_VERIFY_FAILED_MESSAGE);
      }
      const userId = getClerkAuthUser()?.id;

      if (didAutoConsent) {
        analytics.trackEvent({
          name: 'consent_decision',
          properties: { decision: 'analytics_only', variant: 'none', time_to_decision_ms: 0, trigger: 'signup_implied' },
        });
      }

      analytics.trackEvent({ name: 'email_verified', properties: {} });
      
      if (userId) {
        // Check if new user (no profile completion) vs returning user
        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_status')
          .eq('id', userId)
          .single();
        
        const isNewUser = profile?.onboarding_status !== 'completed';
        
        if (isNewUser) {
          // Update profile with intent, attribution, and mark onboarding complete
          const updates: Record<string, unknown> = {
            signup_intent: state.intent,
            onboarding_status: 'completed',
            onboarding_completed_at: new Date().toISOString(),
          };
          // Store terms acceptance timestamp if user accepted during signup
          const termsAcceptedAt = localStorage.getItem('reklaim_terms_accepted_at');
          if (termsAcceptedAt) {
            updates.terms_accepted_at = termsAcceptedAt;
            localStorage.removeItem('reklaim_terms_accepted_at');
          }
          if (options?.source) updates.signup_source = options.source;
          if (options?.medium) updates.signup_medium = options.medium;
          if (options?.campaign) updates.signup_campaign = options.campaign;
          if (options?.fbclid) updates.signup_fbclid = options.fbclid;
          if (options?.campaignId) updates.signup_campaign_id = options.campaignId;
          if (options?.adsetId) updates.signup_adset_id = options.adsetId;
          if (options?.adId) updates.signup_ad_id = options.adId;

          await supabase
            .from('profiles')
            .update(updates)
            .eq('id', userId);

          // Onboarding fields (DOB, first/last name → user_knowledge + profiles.full_name)
          // are persisted by the active auth provider's sign-in handler (Supabase:
          // SIGNED_IN via setSession above; Clerk: runPostSignInEffects after the
          // profile resolves), which covers the OTP path AND the legacy magic-link
          // click path. No need to duplicate that work here.

          // Process referral via edge function (handles points for both users)
          if (options?.referralCode) {
            try {
              const { data: refData, error: refError } = await supabase.functions.invoke('process-referral', {
                body: { referralCode: options.referralCode },
              });
              if (refError) {
                console.error('Failed to process referral:', refError);
              } else if (refData?.success) {
                console.log('Referral processed, points awarded:', refData.pointsAwarded);
                analytics.trackEvent({ name: 'referral_signup_completed', properties: { referring_user_id: refData.referrerId || 'unknown' } });
              }
            } catch (refErr) {
              console.error('Error processing referral:', refErr);
            }
          }

          // Record Terms of Service consent for GDPR compliance (REK-1938:
          // via record-consent edge function — user_consents is now service_role-only for writes).
          try {
            await grantConsent('terms_of_service');
          } catch (consentError) {
            console.error('Failed to record ToS consent:', consentError);
            // Don't block signup if consent recording fails
          }

          // REK-1727: Record marketing-email consent if the user opted in
          // on the Unveil signup form. INDEPENDENT block from the ToS
          // consent above (the two are separate granted_at audit rows).
          // The flag is overwrite-on-submit in
          // `LemonadeOnboarding.handleEmailSubmit` so a stale 'true' from
          // a prior session can never be read here.
          //
          // Magic-link-new-tab caveat: `verifyOtp` does NOT run for users
          // who finish signup by clicking the magic link in a new tab
          // (that path goes through AuthProvider's SIGNED_IN handler).
          // For those users this DB write is skipped — acceptable
          // best-effort: the Braze tag (set on the unveil device) +
          // `email_subscribe: opted_in` + the email-keyed merge in
          // /users/identify all still work, so they remain correctly
          // subscribed and retargetable.
          //
          // The cleanup MUST run regardless of consent value or insert
          // success so a stale flag can't survive into the next signup.
          {
            const marketingConsentFlag = localStorage.getItem('reklaim_marketing_consent');
            if (marketingConsentFlag === 'true') {
              try {
                // REK-1938: via record-consent edge function.
                // The DB enum value is `email_marketing` (added by the
                // 20260108161637 migration via ADD VALUE).
                await grantConsent('email_marketing');
              } catch (marketingConsentError) {
                console.error('Failed to record marketing-email consent:', marketingConsentError);
                // Don't block signup if consent recording fails
              }
            }
            try {
              localStorage.removeItem('reklaim_marketing_consent');
            } catch {
              // ignore
            }
          }

          // Store breach data
          if (state.lookupData?.breachData && state.lookupData.breachData.length > 0) {
            const breachRecords = state.lookupData.breachData.map(breach => ({
              user_id: userId!,
              breach_name: breach.name,
              breach_date: breach.date,
              severity: breach.severity,
              exposed_data_types: breach.dataTypes,
              source: 'hibp',
            }));

            await supabase.from('breach_data').insert(breachRecords);
          }

          // Award signup points securely via edge function
          // Only award base + profile bonus (not breach bonus - that's Protect, not Earn)
          const earnEntries = state.lookupData 
            ? state.lookupData.calculation.base + state.lookupData.calculation.profileBonus
            : state.entriesAwarded;
            
          // Fire-and-forget: these are non-critical background operations.
          // Awaiting them blocks the UI transition to 'complete' — they can timeout
          // (edge runtime wall-clock kill) which silently prevents setState('complete')
          // from ever running, leaving the user stuck on the spinner indefinitely.
          // Retry safety: the stable per-user referenceId below dedups on the
          // (user_id, transaction_type, reference_id) key inside award_points_secure
          // (plus the signup lifetime cap of 1), so a retried invoke after a
          // dropped call credits once, never twice. The key is constant per user
          // because user-role award-points targets the JWT subject.
          if (earnEntries > 0) {
            supabase.functions.invoke('award-points', {
              body: {
                amount: earnEntries,
                transactionType: 'signup',
                referenceId: 'signup:welcome-v1',
                description: 'Signup bonus: welcome + profile data',
              },
            }).catch((pointsError) => {
              console.error('Failed to award signup points:', pointsError);
            });
          }

          const timeToComplete = signupStartedAt.current ? Math.round((Date.now() - signupStartedAt.current) / 1000) : undefined;
          const experimentVariants = getExperimentVariantsMap();
          const entrySource = resolveEntrySource(attributionFromOptions(options));
          analytics.trackEvent({
            name: 'signup_completed',
            properties: {
              origin: 'email',
              time_to_complete: timeToComplete,
              auth_method: 'email_otp',
              experiment_variants: experimentVariants,
              entry_source: entrySource,
            },
          });
          analytics.trackEvent({ name: 'registration_completed', properties: { method: 'email' } });
          // App half of Meta CompleteRegistration via GTM (REK-2146). Consent-
          // gated; Pixel ID stays in the GTM container — never hardcoded here.
          pushCompleteRegistration({
            auth_method: 'email_otp',
            entry_source: entrySource,
            experiment_variants: experimentVariants,
            signup_campaign: options?.campaign,
            signup_campaign_id: options?.campaignId,
          });
        }
      }
      
      setState(prev => ({ ...prev, step: 'complete', isLoading: false }));
      options?.onComplete?.();
    } catch (error) {
      console.error('Magic link verification error:', error);
      // Surface account locked errors to the caller for top-level Alert display
      if (error instanceof Error && error.name === 'AccountLockedError') {
        options?.onAccountLocked?.(error.message);
      }
      setState(prev => ({
        ...prev,
        step: 'magic-link',
        isLoading: false,
        error: error instanceof Error ? error.message : 'Verification failed',
      }));
    }
  }, [state.email, state.otpEmail, state.intent, state.lookupData, state.entriesAwarded, options]);

  const reset = useCallback(() => {
    setState({
      step: 'intent',
      email: null,
      otpEmail: null,
      isExistingUser: false,
      intent: [],
      lookupData: null,
      entriesAwarded: 0,
      isLoading: false,
      error: null,
      linkSent: false,
      resendCooldown: 0,
    });
    brazeTaggedEmails.current.clear();
  }, []);

  return {
    ...state,
    setIntent,
    submitEmail,
    sendOtp,
    verifyOtp,
    reset,
  };
}
